// Resultados de cotización compartidos por todos los cotizadores: barra de
// convenios, filtros (Más Económica / Más Rápida), vistas (cuadrícula / lista /
// compacta), filtro por paquetería y "Reiniciar filtros". Cualquier cambio aquí
// se aplica automáticamente a Menu Cotizar, Carga Manual IA y Landing Page.
import { useEffect, useMemo, useState } from "react";
import RateCard from "@/components/RateCard";
import RateRow from "@/components/RateRow";
import SimpleRateCard from "@/components/quote/SimpleRateCard";
import { getCarrier } from "@/lib/carriers";
import { sortCheapest, sortFastest } from "@/lib/quoteEngine";
import { SOURCE_LEGEND } from "@/lib/quoteConfig";
import { useAggregatorFunding, isSourceFunded } from "@/hooks/useAggregatorFunding";
import { getAggregatorLabel } from "@/lib/aggregatorLabel";
import { Filter, TrendingDown, Zap, X, LayoutGrid, List, AlignJustify, Gift, Truck, AlertCircle } from "lucide-react";
import MobileSelect from "@/components/MobileSelect";
import { useToast } from "@/components/ui/use-toast";
import { useGiftActive } from "@/hooks/useGiftActive";
import { useDisabledSources, rateBelongsToDisabled } from "@/lib/disabledSources";

// Comparación estable de tarifas: markRanks genera copias (spread) en cada
// render, así que `selectedRate === r` (referencia) siempre es false y la
// selección nunca se remarca. Comparamos por la identidad lógica de la tarifa.
const sameRate = (a, b) => {
  if (!a || !b) return false;
  if (a === b) return true;
  return a.carrier === b.carrier
    && (a.source || "interno") === (b.source || "interno")
    && (a.service || "") === (b.service || "")
    && Number(a.total_cost) === Number(b.total_cost);
};

export default function QuoteResults({ rates, srsimiPending, onSelect, quoteId, selectedRate, selectLabel, hideUnavailable = false, cardVariant = "full", dimFiltered = [], compactConvenios = false }) {
  const [filter, setFilter] = useState("economic");
  const [carrierFilter, setCarrierFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState(null);
  const [viewMode, setViewMode] = useState("grid");
  const funding = useAggregatorFunding();
  const { toast } = useToast();

  // Al iniciar una nueva cotización (quoteId cambia), reinicia solo los filtros
  // de convenio y paquetería; conserva el orden (económica/rápida) y la vista.
  useEffect(() => {
    setCarrierFilter("all");
    setSourceFilter(null);
  }, [quoteId]);

  // Reinicia el aviso de tarifas ocultas por dimensiones al cambiar de cotización.
  const [dimNotice, setDimNotice] = useState([]);
  useEffect(() => {
    setDimNotice(Array.isArray(dimFiltered) ? dimFiltered : []);
  }, [quoteId, dimFiltered]);

  const markRanks = (all, currentFilter) => {
    const cheapest = sortCheapest(all)[0];
    const fastest = sortFastest(all)[0];
    return all.map(r => ({
      ...r,
      _rank: r.carrier === cheapest?.carrier && r.total_cost === cheapest?.total_cost ? "cheapest" : "",
      _rankFast: r.carrier === fastest?.carrier && r.transit_days === fastest?.transit_days ? "fastest" : ""
    })).map(r => ({ ...r, _rank: currentFilter === "fast" ? (r._rankFast || "") : (r._rank || "") }));
  };

  const carriersInRates = useMemo(() => [...new Set(rates.map(r => r.carrier))], [rates]);
  const presentSources = useMemo(() => new Set(rates.map(r => r.source).filter(Boolean)), [rates]);
  const { active: giftActive, giftBlocks } = useGiftActive();
  // Convenios apagados por el admin: su chip queda inactivo y no se muestran
  // tarifas suyas (candado extra por si un cotizador entregara alguna).
  const disabledSources = useDisabledSources();
  const isLegendOff = (s) => disabledSources.has(s.key);
  const visibleRates = rates.filter(r => {
    if (rateBelongsToDisabled(r, disabledSources)) return false;
    if (giftBlocks(r)) return false;
    if (hideUnavailable && !isSourceFunded(funding, r.source)) return false;
    if (carrierFilter !== "all" && r.carrier !== carrierFilter) return false;
    if (sourceFilter) {
      const legend = SOURCE_LEGEND.find(s => s.key === sourceFilter);
      const sources = legend?.sources || [sourceFilter];
      if (!sources.includes(r.source)) return false;
    }
    // "Con servicio de recolección": carriers con recolección vía API
    // (rate.pickup.available) más los convenios propios / conexiones directas
    // API propia con badge "SGM" (source mydhl) que destacan por sus
    // servicios adicionales (incluye recolección agendable).
    if (filter === "services" && r.source !== "mydhl" && !(r.pickup && r.pickup.available)) return false;
    return true;
  });
  const hiddenByGift = giftActive ? rates.filter(r => giftBlocks(r)).length : 0;
  const sorted = filter === "fast" ? sortFastest(visibleRates) : sortCheapest(visibleRates);
  const displayRates = markRanks(sorted, filter);

  const hasFilters = sourceFilter || carrierFilter !== "all";
  const resetFilters = () => { setSourceFilter(null); setCarrierFilter("all"); };

  return (
    <>
      {giftActive && hiddenByGift > 0 && (
        <div className="mb-2 surface-inset px-3 py-2 text-[0.7rem] text-cyan border-l-2 border-cyan flex items-center gap-1.5">
          <Gift className="w-3.5 h-3.5 shrink-0" />
          Usando saldo de regalo: carriers limitados. <span className="text-ink-muted">Recarga para ver todos los carriers.</span>
        </div>
      )}
      {dimNotice.length > 0 && (
        <div className="mb-2 surface-inset px-3 py-2 border-l-2 border-warning text-[0.7rem] text-ink-text flex items-start gap-1.5">
          <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5 text-warning" />
          <span>
            <strong className="text-warning">Algunas tarifas se ocultaron por dimensiones:</strong>{" "}
            {dimNotice.map((d, i) => (
              <span key={i}>
                {d.carrier}{d.source && d.source !== "interno" ? ` (${getAggregatorLabel(d) || d.source})` : ""}
                {i < dimNotice.length - 1 ? ", " : ""}
              </span>
            ))}
            . Ajusta el tipo de envío o las dimensiones para ver más opciones.
          </span>
        </div>
      )}
      {srsimiPending && (
        <div className="mb-2 flex items-center gap-2 text-[0.7rem] text-ink-muted">
          <div className="w-3 h-3 border border-rose-400 border-t-transparent rounded-full animate-spin" />
          Buscando opciones de SrSimi…
        </div>
      )}
      {compactConvenios ? (
        <div className="mb-2 flex items-center gap-2 min-w-0">
          <span className="label shrink-0">Convenio</span>
          <MobileSelect
            value={sourceFilter || "all"}
            onChange={(v) => setSourceFilter(v === "all" ? null : v)}
            className="bg-ink border border-ink-border px-2.5 py-1.5 text-xs text-ink-text focus:border-cyan focus:outline-none flex-1 min-w-0 max-w-[16rem]"
            options={[
              { value: "all", label: "Todos los convenios" },
              ...SOURCE_LEGEND
                .map(s => {
                  const srcs = s.sources || [s.key];
                  const hasResults = srcs.some(src => presentSources.has(src));
                  const funded = srcs.every(src => isSourceFunded(funding, src)) && !isLegendOff(s);
                  return { value: s.key, label: s.label, disabled: !funded || !hasResults };
                })
            ]}
          />
          {sourceFilter && (
            <button type="button" onClick={() => setSourceFilter(null)}
              className="flex items-center gap-1 text-[0.7rem] text-cyan hover:underline font-medium shrink-0">
              <X className="w-3 h-3" /> Quitar
            </button>
          )}
        </div>
      ) : (
      <div className="mb-3 flex flex-wrap items-center gap-2 surface-inset px-3 py-2 text-[0.7rem] min-w-0">
        <span className="label">Convenios:</span>
        {SOURCE_LEGEND.map(s => {
          const active = sourceFilter === s.key;
          const srcs = s.sources || [s.key];
          const hasResults = srcs.some(src => presentSources.has(src));
          const off = isLegendOff(s);
          const funded = srcs.every(src => isSourceFunded(funding, src)) && !off;
          const clickable = funded && hasResults;
          const reason = off ? "desactivado por el administrador" : !funded ? "sin saldo" : !hasResults ? "sin cobertura para esta ruta" : "";
          const tag = !funded ? "· No disponible" : !hasResults ? "· sin cobertura" : "";
          const handleChipClick = () => {
            if (clickable) return setSourceFilter(active ? null : s.key);
            toast({
              title: `${s.label}`,
              description: "El convenio no está disponible"
            });
          };
          return (
            <button key={s.key} type="button"
              onClick={handleChipClick}
              title={reason ? `${s.label} — ${reason}` : ""}
              className={`flex items-center gap-1.5 px-1.5 py-0.5 border transition-colors ${active ? "border-cyan text-cyan bg-cyan/10" : clickable ? "border-transparent text-ink-muted hover:text-ink-text" : "border-transparent text-ink-muted/40 cursor-not-allowed"}`}>
              <span className={`w-2 h-2 ${s.dot} ${clickable ? "" : "opacity-40"}`} /> {s.label}{tag}
            </button>
          );
        })}
        {hasFilters && (
          <button type="button" onClick={resetFilters}
            className="flex items-center gap-1 text-cyan hover:underline font-medium">
            <X className="w-3 h-3" /> Reiniciar filtros
          </button>
        )}
      </div>
      )}
      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between min-w-0">
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          <Filter className="w-4 h-4 text-ink-muted shrink-0" />
          <span className="label shrink-0">Filtrar</span>
          <FilterBtn active={filter === "economic"} onClick={() => setFilter("economic")} icon={TrendingDown}>Más Económica</FilterBtn>
          <FilterBtn active={filter === "fast"} onClick={() => setFilter("fast")} icon={Zap}>Más Rápida</FilterBtn>
          <FilterBtn active={filter === "services"} onClick={() => setFilter("services")} icon={Truck}>Con servicio de recolección</FilterBtn>
        </div>
        <div className="flex flex-wrap items-center gap-3 min-w-0">
          <div className="flex gap-1 shrink-0">
            <ViewBtn active={viewMode === "grid"} onClick={() => setViewMode("grid")} icon={LayoutGrid} title="Vista cuadrícula" />
            <ViewBtn active={viewMode === "list"} onClick={() => setViewMode("list")} icon={List} title="Vista lista" />
            <ViewBtn active={viewMode === "compact"} onClick={() => setViewMode("compact")} icon={AlignJustify} title="Vista compacta" />
          </div>
          <div className="flex items-center gap-2 min-w-0">
            <span className="label shrink-0">Paquetería</span>
            <MobileSelect
              value={carrierFilter}
              onChange={setCarrierFilter}
              className="bg-ink border border-ink-border px-2.5 py-1.5 text-xs text-ink-text focus:border-cyan focus:outline-none w-full sm:w-auto min-w-0 max-w-[60vw] sm:max-w-[12rem]"
              options={[{ value: "all", label: "Todas" }, ...carriersInRates.map(c => ({ value: c, label: getCarrier(c).name }))]}
            />
          </div>
          {hasFilters && (
            <button type="button" onClick={resetFilters}
              className="flex items-center gap-1 text-[0.7rem] text-cyan hover:underline font-medium">
              <X className="w-3 h-3" /> Reiniciar filtros
            </button>
          )}
        </div>
      </div>
      {cardVariant === "simple" ? (
        viewMode === "compact" ? (
          <div className="space-y-1.5">
            {displayRates.map((r, i) => (
              <SimpleRateCard key={r.carrier + r.source + i} rate={r} onSelect={onSelect} selected={sameRate(selectedRate, r)} selectLabel={selectLabel} funded={isSourceFunded(funding, r.source)} compact />
            ))}
          </div>
        ) : (
          <div className={viewMode === "list" ? "space-y-2" : "grid sm:grid-cols-2 gap-2"}>
            {displayRates.map((r, i) => (
              <SimpleRateCard key={r.carrier + r.source + i} rate={r} onSelect={onSelect} selected={sameRate(selectedRate, r)} selectLabel={selectLabel} funded={isSourceFunded(funding, r.source)} />
            ))}
          </div>
        )
      ) : viewMode === "compact" ? (
        <div className="space-y-1.5">
          {displayRates.map((r, i) => (
            <RateRow key={r.carrier + r.source + i} rate={r} onSelect={onSelect} selected={sameRate(selectedRate, r)} selectLabel={selectLabel} funded={isSourceFunded(funding, r.source)} />
          ))}
        </div>
      ) : (
        <div className={viewMode === "list" ? "space-y-3" : "grid sm:grid-cols-2 gap-3"}>
          {displayRates.map((r, i) => (
            <RateCard key={r.carrier + r.source + i} rate={r} onSelect={onSelect} selected={sameRate(selectedRate, r)} selectLabel={selectLabel} funded={isSourceFunded(funding, r.source)} />
          ))}
        </div>
      )}
    </>
  );
}

function FilterBtn({ active, onClick, icon: Icon, children }) {
  return (
    <button onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border ${
        active ? "border-cyan text-cyan bg-cyan/5" : "border-ink-border text-ink-muted hover:text-ink-text"
      }`}>
      <Icon className="w-3.5 h-3.5" /> {children}
    </button>
  );
}

function ViewBtn({ active, onClick, icon: Icon, title }) {
  return (
    <button onClick={onClick} title={title}
      className={`p-1.5 border ${active ? "border-cyan text-cyan" : "border-ink-border text-ink-muted hover:text-ink-text"}`}>
      <Icon className="w-3.5 h-3.5" />
    </button>
  );
}